import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Activity, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddSymptomDialog from './AddSymptomDialog';

const SymptomsTab = () => {
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchSymptoms();
  }, []);

  const fetchSymptoms = async () => {
    try {
      const response = await API.get('/medical/symptoms');
      setSymptoms(response.data);
    } catch (error) {
      console.error('Failed to fetch symptoms:', error);
      toast.error('Failed to load symptoms');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (symptomData) => {
    try {
      await API.post('/medical/symptoms', symptomData);
      toast.success('Symptom added');
      fetchSymptoms();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add symptom');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/symptoms/${id}`);
      toast.success('Symptom deleted');
      fetchSymptoms();
    } catch (error) {
      toast.error('Failed to delete symptom');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (symptoms.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <Activity className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No symptoms recorded</h3>
          <p className="text-gray-600 mb-4">Track signs and symptoms over time</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Symptom
          </Button>
        </div>
        <AddSymptomDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Signs & Symptoms</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Symptom
        </Button>
      </div>

      <div className="space-y-4">
        {symptoms.map((symptom) => (
          <div
            key={symptom.id}
            className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{symptom.description}</h4>
                <div className="flex items-center space-x-3 mt-1">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-1" />
                    {symptom.start_date}
                  </div>
                  {symptom.severity && (
                    <Badge 
                      className={
                        symptom.severity === 'severe' ? 'bg-red-100 text-red-800' :
                        symptom.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }
                    >
                      {symptom.severity}
                    </Badge>
                  )}
                  {symptom.ongoing ? (
                    <Badge className="bg-blue-100 text-blue-800">Ongoing</Badge>
                  ) : (
                    <Badge variant="outline">Resolved</Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
              {symptom.what_helps && (
                <div>
                  <span className="text-gray-600 font-medium">What helps:</span>
                  <p className="text-gray-900">{symptom.what_helps}</p>
                </div>
              )}
              {symptom.what_worsens && (
                <div>
                  <span className="text-gray-600 font-medium">What worsens:</span>
                  <p className="text-gray-900">{symptom.what_worsens}</p>
                </div>
              )}
              {symptom.medications_taken && (
                <div>
                  <span className="text-gray-600 font-medium">Medications taken:</span>
                  <p className="text-gray-900">{symptom.medications_taken}</p>
                </div>
              )}
            </div>

            {symptom.notes && (
              <div className="p-3 bg-gray-50 rounded-lg mb-3">
                <p className="text-sm text-gray-700">{symptom.notes}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(symptom.id)}
                className="text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddSymptomDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default SymptomsTab;